/**
 * T10 语音对话（UI 组装）类型契约。
 *
 * 本层只做「UI 串联」：TTS 适配器来自 `src/core/tts`，转写适配器来自
 * `src/core/voice`，录音能力抽象为可注入的 `Recorder`。所有运行期依赖
 * （TTS 适配器 / 转写适配器 / 录音器 / 音频元素工厂）一律通过参数注入，
 * 便于单测替换为 fake —— 测试不联网、不调用真实麦克风。
 */

import type { TtsAdapter, TtsRequest, TtsResult } from '../../core/tts';
import type { TranscribeAdapter, TranscribeRequest, TranscribeResult } from '../../core/voice';

/** TTS 播放状态机：空闲 / 加载中 / 播放中 / 失败（四态）。 */
export type TtsPlaybackState = 'idle' | 'loading' | 'playing' | 'error';

/** 录音状态机：空闲 / 录音中 / 转写中 / 失败。 */
export type RecordingState = 'idle' | 'recording' | 'transcribing' | 'error';

/** 结构化错误的来源。 */
export type VoiceIssueSource = 'tts' | 'transcribe' | 'recorder';

/**
 * 结构化错误：适配器 / 录音失败时一律转成它，UI 只渲染 `message`，
 * 断言只依赖 `code`，绝不让异常穿透到 React 渲染树导致白屏。
 */
export interface VoiceIssue {
  source: VoiceIssueSource;
  /** 规范化错误码：沿用 core 适配器的 `http` / `timeout` / `network` … 或 `config` / `aborted` / `unknown`。 */
  code: string;
  message: string;
}

/**
 * 录音能力抽象（可注入）。默认实现见 `recorder.ts`（`navigator.mediaDevices`）。
 */
export interface Recorder {
  /** 申请麦克风并开始录音。 */
  start(): Promise<void>;
  /** 结束录音并返回音频二进制。 */
  stop(): Promise<Blob>;
  /** 放弃当前录音（释放音轨，不产出结果）。 */
  cancel(): void;
}

/** 音频元素工厂；默认 `() => new Audio()`，测试注入 fake 以避免真实播放。 */
export type AudioFactory = () => HTMLAudioElement;

/** Object URL 适配器；默认走 `URL.createObjectURL`，测试可替换以脱离浏览器全局。 */
export interface ObjectUrlAdapter {
  create(blob: Blob): string;
  revoke(url: string): void;
}

/** 语音能力对外依赖（全部可注入，缺省为 `null` 时给出结构化「未配置」错误）。 */
export interface VoiceDeps {
  /** TTS 适配器（`src/core/tts`）。 */
  tts: TtsAdapter | null;
  /** 转写适配器（`src/core/voice`）。 */
  transcribe: TranscribeAdapter | null;
  /** 录音器；不支持录音的环境为 `null`。 */
  recorder: Recorder | null;
  /** 覆盖默认音频元素工厂（测试用）。 */
  audioFactory?: AudioFactory;
  /** 覆盖默认 Object URL 实现（测试用）。 */
  objectUrl?: ObjectUrlAdapter;
}

/** 语音对话模式开关的 localStorage 键（需求约定）。 */
export const VOICE_MODE_STORAGE_KEY = 't10.voiceMode';

/** 只读 / 可写的键值存储最小接口（默认 `localStorage`，测试可注入内存实现）。 */
export interface ReadableStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export type { TtsAdapter, TtsRequest, TtsResult, TranscribeAdapter, TranscribeRequest, TranscribeResult };